/**
 * DatePicker — поле даты с календарём.
 *
 * Устроен как `Select`: триггер — `Input` (он же остаётся полем ввода, дату
 * можно набрать руками), календарь — всплывающая поверхность на токенах.
 * Теней в системе нет, поэтому слой разводится поверхностью и границей, а
 * порядок наложения берётся из `ZDropdown`.
 *
 * Ввод руками не отнимаем: дату из письма быстрее набрать, чем долистать
 * календарём, а календарь нужен, когда день ищут глазами («последняя
 * пятница месяца»). Поэтому поле остаётся текстовым, а значок календаря —
 * кнопкой рядом.
 *
 * Значение ходит строкой `ДД.ММ.ГГГГ` — в этом виде оно лежит в контракте
 * отчёта. Непригодную строку контрол не подменяет: показывает как есть,
 * календарь просто открывается на текущем месяце.
 *
 * @format
 */

import {useMemo, useState, type ChangeEvent} from 'react';

import {Box, Popover} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {Input, type InputSize} from 'ui/components/atoms/Input';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {DS_SCOPE_CLASS} from 'ui/theme/scope';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {MotionDurationBase, MotionEasingStandard} from 'ui/tokens/ts/motion';
import {ZDropdown} from 'ui/tokens/ts/elevation';
import {RadiusM} from 'ui/tokens/ts/radii';

import {
  MONTHS_NOMINATIVE,
  WEEKDAYS,
  formatDate,
  isOutOfRange,
  isSameDay,
  monthGrid,
  parseDate,
  shiftMonth,
} from './dateValue';

export interface DatePickerProps {
  /** Дата строкой `ДД.ММ.ГГГГ`. Пусто — поле пустое. */
  value?: string;
  /** Новое значение, в том же формате. */
  onChange?: (value: string) => void;
  /** Подсказка в пустом поле. По умолчанию — маска формата. */
  placeholder?: string;
  /** Нижняя граница выбора, `ДД.ММ.ГГГГ`. Включительно. */
  min?: string;
  /** Верхняя граница выбора, `ДД.ММ.ГГГГ`. Включительно. */
  max?: string;
  /** Ступень высоты — та же, что у `Input`. */
  size?: InputSize;
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Id поля — для `FormField htmlFor`. */
  id?: string;
  ariaLabel?: string;
  className?: string;
}

/** Размер клетки дня. Ниже 40 палец уже промахивается. */
const CELL = 40;

const Anchor = styled(Box)({
  minWidth: 0,
});

const Sheet = styled(Popover)({
  '& .MuiPaper-root': {
    backgroundColor: surfaceVar('surface2'),
    color: surfaceVar('onSurface'),
    border: `1px solid ${surfaceVar('surface3')}`,
    borderRadius: RadiusM,
    backgroundImage: 'none',
    boxShadow: 'none',
    marginTop: spacingPx('xs'),
    padding: spacingPx('m'),
    // Остаток высоты отдаём прокрутке — сам предел считается при открытии
    // (см. `MIN_SHEET_HEIGHT`), потому что зависит от места под полем.
    overflowY: 'auto',
  },
});

/**
 * Календарь не сжимается ниже этого предела.
 *
 * Шесть недель по клетке плюс шапка — 372 пикселя; на невысоком экране они
 * не помещаются, и остаток уходит в прокрутку. Но предел, посчитанный от
 * места под полем, в вырожденном случае (окно схлопнуто, высота нулевая)
 * обнулял бы и сам календарь — поэтому ниже этой границы не опускаемся, а
 * позиционирование дальше делает `Popover`.
 */
const MIN_SHEET_HEIGHT = 240;

const MonthLabel = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

const NavButton = styled('button')(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: CELL,
  height: CELL,
  padding: 0,
  border: 'none',
  borderRadius: RadiusM,
  background: 'transparent',
  color: surfaceVar('onSurfaceLight'),
  cursor: 'pointer',
  transition: `background-color ${MotionDurationBase} ${MotionEasingStandard}`,
  '&:hover': {backgroundColor: surfaceVar('surface3')},
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: `repeat(7, ${CELL}px)`,
  gap: spacingPx('xxs'),
});

const Weekday = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  height: CELL,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * День месяца. Выбранный — заливка роли, сегодняшний — только рамка: он
 * подсказка, а не выбор, и спорить с выбранным цветом ему нельзя.
 */
const Day = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$selected: boolean; $today: boolean; $outside: boolean}>(
  ({theme, $selected, $today, $outside}) => ({
    ...text('body.l'),
    width: CELL,
    height: CELL,
    padding: 0,
    border: $today && !$selected ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
    borderRadius: RadiusM,
    cursor: 'pointer',
    transition: `background-color ${MotionDurationBase} ${MotionEasingStandard}`,
    backgroundColor: $selected ? theme.palette.primary.main : 'transparent',
    color: $selected
      ? theme.palette.primary.contrastText
      : $outside
        ? surfaceVar('onSurfaceMuted')
        : surfaceVar('onSurface'),
    '&:hover:not(:disabled)': {
      backgroundColor: $selected ? theme.palette.primary.hover : surfaceVar('surface3'),
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&:disabled': {
      color: theme.palette.disabledStates.high,
      cursor: 'not-allowed',
    },
  }),
);

export function DatePicker({
  value,
  onChange,
  placeholder = 'ДД.ММ.ГГГГ',
  min,
  max,
  size = 'm',
  fullWidth = false,
  disabled = false,
  error = false,
  id,
  ariaLabel,
  className,
}: DatePickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const selected = parseDate(value);
  const minDate = parseDate(min);
  const maxDate = parseDate(max);

  // Месяц, открытый в календаре. Стартует от выбранной даты, а если её нет —
  // от нижней границы: там, где выбор вообще возможен.
  const [month, setMonth] = useState<Date>(() => selected ?? minDate ?? new Date());

  const days = useMemo(() => monthGrid(month), [month]);
  const today = useMemo(() => new Date(), []);

  const [sheetHeight, setSheetHeight] = useState(MIN_SHEET_HEIGHT);

  const open = (event: {currentTarget: HTMLElement}) => {
    if (disabled) return;
    setMonth(selected ?? minDate ?? new Date());
    // Сколько места осталось под полем — столько календарь и занимает.
    const room = window.innerHeight - event.currentTarget.getBoundingClientRect().bottom;
    setSheetHeight(Math.max(MIN_SHEET_HEIGHT, room - spacingPx('l')));
    setAnchor(event.currentTarget);
  };

  const pick = (day: Date) => {
    setAnchor(null);
    onChange?.(formatDate(day));
  };

  const handleType = (event: ChangeEvent<HTMLInputElement>) => onChange?.(event.target.value);

  return (
    <Anchor className={className}>
      <Input
        id={id}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={handleType}
        clearable={false}
        rightIcon='calendar_month'
        onRightIconClick={open}
        rightIconLabel='Открыть календарь'
        aria-label={ariaLabel}
      />

      <Sheet
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        style={{zIndex: ZDropdown}}
        PaperProps={{className: DS_SCOPE_CLASS, style: {maxHeight: sheetHeight}}}
      >
        <Stack gap='s'>
          <Row justify='between' alignY='center'>
            <NavButton
              type='button'
              onClick={() => setMonth(shiftMonth(month, -1))}
              aria-label='Предыдущий месяц'
            >
              <Icon name='chevron_left' size='s' color='inherit' />
            </NavButton>
            <MonthLabel>
              {MONTHS_NOMINATIVE[month.getMonth()]} {month.getFullYear()}
            </MonthLabel>
            <NavButton
              type='button'
              onClick={() => setMonth(shiftMonth(month, 1))}
              aria-label='Следующий месяц'
            >
              <Icon name='chevron_right' size='s' color='inherit' />
            </NavButton>
          </Row>

          <Grid role='grid'>
            {WEEKDAYS.map(day => (
              <Weekday key={day}>{day}</Weekday>
            ))}
            {days.map(day => {
              const outside = day.getMonth() !== month.getMonth();
              const blocked = isOutOfRange(day, minDate, maxDate);
              return (
                <Day
                  key={day.toISOString()}
                  type='button'
                  $selected={selected != null && isSameDay(day, selected)}
                  $today={isSameDay(day, today)}
                  $outside={outside}
                  disabled={blocked}
                  aria-label={formatDate(day)}
                  aria-current={isSameDay(day, today) ? 'date' : undefined}
                  onClick={() => pick(day)}
                >
                  {day.getDate()}
                </Day>
              );
            })}
          </Grid>
        </Stack>
      </Sheet>
    </Anchor>
  );
}
